// Centralized local and session storage service
import { STORAGE_KEYS } from "@/constants/auth.constants";

const SENSITIVE_KEYS = new Set([
  "accessToken",
  "token",
  "refreshToken",
  STORAGE_KEYS.ACCESS_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
]);

const isSensitive = (key) => SENSITIVE_KEYS.has(key);

class StorageService {
  constructor() {
    this._memoryStore = new Map();
    // Immediate XSS hardening: sanitize and purge sensitive tokens from persistent localStorage
    try {
      SENSITIVE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage unavailable or restricted
    }
  }

  getItem(key) {
    if (isSensitive(key)) {
      if (this._memoryStore.has(key)) {
        return this._memoryStore.get(key);
      }
      try {
        const val = sessionStorage.getItem(key);
        if (val) {
          this._memoryStore.set(key, val);
          return val;
        }
      } catch {
        // Fallback to null on storage error
      }
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setItem(key, value) {
    if (isSensitive(key)) {
      this._memoryStore.set(key, value);
      try {
        // Purge from localStorage to prevent XSS exfiltration
        localStorage.removeItem(key);
        sessionStorage.setItem(key, value);
      } catch {
        // Storage write error handled safely
      }
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage write error handled safely
    }
  }

  removeItem(key) {
    if (isSensitive(key)) {
      this._memoryStore.delete(key);
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch {
        // Storage remove error handled safely
      }
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage remove error handled safely
    }
  }

  clear() {
    this._memoryStore.clear();
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage clear error handled safely
    }
  }

  getSessionItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setSessionItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage write error handled safely
    }
  }

  removeSessionItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage remove error handled safely
    }
  }

  clearSession() {
    try {
      sessionStorage.clear();
    } catch {
      // Storage clear error handled safely
    }
  }

  getAccessToken() {
    return this.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  setAccessToken(token) {
    this.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  removeAccessToken() {
    this.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken() {
    return this.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  setRefreshToken(token) {
    this.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  removeRefreshToken() {
    this.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getUserType() {
    return this.getItem(STORAGE_KEYS.USER_TYPE);
  }

  setUserType(type) {
    this.setItem(STORAGE_KEYS.USER_TYPE, type);
  }

  removeUserType() {
    this.removeItem(STORAGE_KEYS.USER_TYPE);
  }

  getUserEmail() {
    return this.getItem(STORAGE_KEYS.USER_EMAIL) || this.getItem("email");
  }

  setUserEmail(email) {
    this.setItem(STORAGE_KEYS.USER_EMAIL, email);
  }

  removeUserEmail() {
    this.removeItem(STORAGE_KEYS.USER_EMAIL);
    this.removeItem("email");
  }

  getJSON(key) {
    try {
      const val = this.getItem(key);
      return val ? JSON.parse(val) : null;
    } catch {
      return null;
    }
  }

  setJSON(key, obj) {
    try {
      this.setItem(key, JSON.stringify(obj));
    } catch {
      // Storage write error handled safely
    }
  }

  clearAuth() {
    this.removeAccessToken();
    this.removeRefreshToken();
    this.removeUserType();
    this.removeUserEmail();
    this.removeItem(STORAGE_KEYS.CURRENT_USER);
    this.removeSessionItem("msal.interaction.status");
    this.removeSessionItem("authUser");
  }
}

export const storageService = new StorageService();
export default storageService;
